import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Progress from "@/components/Progress";
import SegmentedButton from "@/components/SegmentedButton";
import { SegmentedButtonOption } from "@/components/SegmentedButton/SegmentedButton";
import Select from "@/components/Select";
import SideSheetModal from "@/components/SideSheetModal";
import TextField from "@/components/TextField";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import {
  refetchPersonInteractionViews,
  shouldRefetchPersonInteractionViewsForGroupCreate,
  shouldRefetchPersonInteractionViewsForSingleCreate
} from "@/components/interactions/refetchPersonInteractionViews";
import {
  CREATE_INTERACTION_MUTATION,
  CREATE_GROUP_INTERACTION_MUTATION,
  INTERACTION_CATEGORIES_METADATA_QUERY,
  type BackofficeCRMCreateInteractionInput,
  type BackofficeCRMCreateGroupInteractionInput,
  type InteractionCategoriesMetadataData
} from "@/components/interactions/queries";
import { INTERACTION_ANALYTICS_EVENTS, INTERACTION_CREATE_SOURCES } from "@/components/interactions/amplitude";
import useInteractionModes from "@/components/interactions/useInteractionModes";
import { InteractionInput } from "@/components/interactions/types";
import { PERSON_WITH_SALESFORCE_ID_QUERY } from "@/pages/people/queries";
import { JOB_POST_WITH_INTERACTIONS_QUERY, JOB_POST_DETAILS_QUERY } from "@/pages/_layouts/jobs/queries";
import { PEOPLE_WITH_FACILITY_ID, REGISTRANTS_FROM_EVENT_ID, PEOPLE_FROM_IDS } from "./queries";
import { useMutation, useQuery, useApolloClient } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { useTopActionBar } from "@/layouts/AppLayout/TopActionBarContext";
import { ObjectHeadingWrapper } from "@/features/person/components/PersonObjectHeading/PersonObjectHeading";
import Icon from "@/components/Icon";
import { GroupInteraction } from "./GroupInteraction";
import { MultiPersonAdder } from "./MultiPersonAdder";
import { SinglePerson } from "./SinglePerson";
import { useAiQuery, formatAiSummary, guardAiResponse } from "@/lib/aiHelpers";
import { SummaryPayload } from "@/components/interactions/types";
import Switch from "@/components/Switch";
import AttachTaskSection from "@/components/AttachTaskSection";

import useAttachTask from "@/hooks/useAttachTask";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import useAmplitude from "@/hooks/useAmplitude";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useHasRoles, useCanManageJobPostsBool, useCanManageTasksBool } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import AlgoliaJobsSearch from "@/components/JobsSearch/AlgoliaJobsSearch";
import MultiSelect from "@/components/MultiSelect";
import {
  selectionRequiresDefaultPrivateTopics,
  shouldUpdatePrivacyForSelection
} from "@/components/interactions/interactionCategoryPrivacy";
import { isEmpty, reject } from "lodash";

const COMPENSATION_NEGOTIATION_CATEGORY = "Compensation/Negotiation";
const COMPENSATION_NEGOTIATION_TASK_NAME = "Follow-up on Compensation Changes";

function compensationNegotiationTaskDueDate() {
  const dueDate = new Date();
  dueDate.setMonth(dueDate.getMonth() + 2);
  return { startDate: dueDate, endDate: dueDate };
}

export type Person = {
  id: string;
  pgaId: string;
  firstName: string;
  lastName: string;
  sectionName: string;
  facilityName: string;
  profilePhoto: string;
  selected: boolean;
  index: number;
  primaryFacility: { name: string; id: string };
  primarySectionAffiliation: { section: { name: string } };
  eventId?: string;
  email?: string;
};

export type Event = {
  title: string;
  id: string;
  slug: string;
  index: number;
  startsAt: string;
  endsAt: string;
  city?: string;
  state?: string;
  facility?: {
    id: string;
    name: string;
  };
};

export type Facility = {
  name: string;
  id: string;
  index: number;
  mainAddress: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    postalCode: string;
    countryCode: string;
    isVerified: boolean;
  };
};

export type User = {
  name: string;
  email: string;
};

export const CreateGlobalInteractionSideSheet = ({
  sidebarOpen,
  setSidebarOpen
}: {
  sidebarOpen: boolean;
  setSidebarOpen: (value: boolean) => void;
}) => {
  // TODO: The below check is temporary while the PGA Agent is adjusted for other departments.
  const isCareerServices = useHasRoles([
    ROLE.GLOBAL_ADMIN,
    ROLE.CAREER_SERVICES_STAFF,
    ROLE.RECRUITING_STAFF,
    ROLE.TECHNOLOGY_STAFF
  ]);
  const canManageJobPosts = useCanManageJobPostsBool();
  const currentUser = useCurrentBackofficeUser();
  const canManageTasks = useCanManageTasksBool();

  const taskHook = useAttachTask(currentUser);
  const { attachTask, setTaskIsPrivate } = taskHook;

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { sidebarPayload } = useTopActionBar();

  const personMatch = matchPath("/people/:pgaId/*", pathname);
  const lockedPgaIdRef = useRef<string | undefined>();
  const lockedJobPostIdRef = useRef<string | undefined>();

  if (sidebarOpen && !lockedPgaIdRef.current) {
    lockedPgaIdRef.current = sidebarPayload?.pgaId || personMatch?.params.pgaId;
    lockedJobPostIdRef.current = sidebarPayload?.defaultJobPostId;
  }

  const resolvedPgaId = lockedPgaIdRef.current;
  const resolvedJobPostId = lockedJobPostIdRef.current;
  const [clearedPreSelection, setClearedPreSelection] = useState(false);
  const hasPreSelectedPerson = !!resolvedPgaId && !clearedPreSelection;

  const apolloClient = useApolloClient();
  const { trackEvent } = useAmplitude();
  const { fetchData: generateSummary, loading: generatingAiSummary } = useAiQuery<SummaryPayload>(
    "career-consultation-summarizer"
  );
  const { fetchData: generateCategory, loading: generatingAiCategory } = useAiQuery<SummaryPayload>(
    "career-consultation-categorizer"
  );
  const defaultInteractionDate = new Date();
  const defaultMode = isCareerServices ? "In-Person" : "Phone Call";
  const { modes: modeOptions } = useInteractionModes(defaultMode, !sidebarOpen);

  const { control, handleSubmit, setValue, getValues, trigger } = useForm({
    mode: "onChange",
    defaultValues: {
      interactionPerson: "",
      interactionMethod: defaultMode,
      interactionTopic: [],
      interactionDescription: "",
      interactionDate: {
        startDate: defaultInteractionDate,
        endDate: defaultInteractionDate
      },
      interactionManualPerson: "",
      interactionFacility: "",
      interactionPeople: "",
      interactionPrivacy: false
    }
  });
  const audienceOptions: SegmentedButtonOption[] = [
    { label: "Single", value: "single" },
    { label: "Group", value: "group" }
  ];
  const [interactionAudience, setInteractionAudience] = useState(audienceOptions[0].value);
  const [peoplePGAId, setPeoplePGAId] = useState<String>();
  const [currentFacility, setCurrentFacility] = useState<Facility | undefined>();
  const [gatheredFacilities, setGatheredFacilities] = useState<Facility[]>([]);
  const [gatheredEvents, setGatheredEvents] = useState<Event[]>([]);
  const [eventRegistrants, setEventRegistrants] = useState<Person[]>([]);
  const [currentEvent, setCurrentEvent] = useState<Event | undefined>();
  const [ineligibleEvent, setIneligibleEvent] = useState<Event | undefined>();
  const [ineligibleFacility, setIneligibleFacility] = useState<Facility | undefined>();
  const [multiPersonMode, setMultiPersonMode] = useState(false);
  const [peopleArray, setPeopleArray] = useState<Person[]>([]);
  const [searchedForPerson, setSearchedForPerson] = useState(false);
  const [ineligiblePerson, setIneligiblePerson] = useState<Person | undefined>();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [variables, setVariables] = useState<InteractionInput>({
    interactionDate: defaultInteractionDate.toISOString().split("T")[0],
    mode: defaultMode,
    activityType: null,
    categories: [],
    description: null,
    participantId: null,
    privacy_restricted: false
  });

  const [createInteractionMutation, { loading: creatingInteraction }] = useMutation(CREATE_INTERACTION_MUTATION);
  const [createGroupInteractionMutation, { loading: creatingGroupInteraction }] = useMutation(
    CREATE_GROUP_INTERACTION_MUTATION
  );
  const createMutationLoading = creatingInteraction || creatingGroupInteraction;
  const { isCompactLayout } = useIsCompactLayout();
  const handleAudienceChange = (audience: string) => {
    if (interactionAudience !== audience) {
      setInteractionAudience(audience);
      resetSinglePersonSearch();
      resetGroupPersonSearch();
    }
  };

  const validatePeopleField = () => {
    setTimeout(() => {
      trigger("interactionPeople");
    }, 100);
  };

  const resetSinglePersonSearch = () => {
    setSearchedForPerson(false);
    setPeoplePGAId(undefined);
    setVariables((prev) => ({ ...prev, participantId: null }));
    setValue("interactionPerson", "");
  };

  const clearPreSelectedPerson = () => {
    setClearedPreSelection(true);
    resetSinglePersonSearch();
  };

  const applyCompensationNegotiationTaskDefaults = (taskShouldBePrivate: boolean) => {
    if (!canManageTasks) {
      return;
    }

    taskHook.toggleAttachTask(true, COMPENSATION_NEGOTIATION_TASK_NAME);
    taskHook.setTaskDueDate(compensationNegotiationTaskDueDate());
    if (taskShouldBePrivate) {
      taskHook.setTaskIsPrivate(true);
    }
  };

  const resetGroupPersonSearch = () => {
    setGatheredFacilities([]);
    setGatheredEvents([]);
    setIneligibleFacility(undefined);
    setPeopleArray([]);
    setSearchedForPerson(false);
    setValue("interactionFacility", "");
    setValue("interactionManualPerson", "");
    setVariables((prev) => ({ ...prev, participantIds: null }));
  };

  const { data: allCategoriesData, loading: loadingCategories } = useQuery<InteractionCategoriesMetadataData>(
    INTERACTION_CATEGORIES_METADATA_QUERY,
    {
      fetchPolicy: "cache-first",
      skip: !sidebarOpen
    }
  );

  const interactionCategoriesMetadata = useMemo(
    () => allCategoriesData?.interactionCategories ?? [],
    [allCategoriesData?.interactionCategories]
  );

  const validCategoryNames = useMemo(
    () => interactionCategoriesMetadata.map((c) => c.name),
    [interactionCategoriesMetadata]
  );

  useEffect(() => {
    if (!sidebarOpen) {
      return;
    }
    const jobLinked = Boolean(variables.jobPostId?.length);
    if (jobLinked) {
      setVariables((prev) => ({ ...prev, privacy_restricted: true }));
      setValue("interactionPrivacy", true);
    }
  }, [sidebarOpen, variables.jobPostId, setValue, setVariables]);

  useEffect(() => {
    if (!sidebarOpen) {
      return;
    }

    const interactionIsPrivate = Boolean(variables.jobPostId) || Boolean(variables.privacy_restricted);
    if (interactionIsPrivate) {
      setTaskIsPrivate(true);
    }
  }, [attachTask, setTaskIsPrivate, sidebarOpen, variables.jobPostId, variables.privacy_restricted]);

  function addPerson(person: Person) {
    if (!peopleArray.some((existingPerson) => existingPerson.id === person.id)) {
      setPeopleArray((prevPeopleArray) => {
        const updatedArray = [...prevPeopleArray, person];
        if (person.selected) {
          setVariables((prevVariables) => ({
            ...prevVariables,
            participantIds: [...(prevVariables.participantIds || []), person.id]
          }));
        }
        return updatedArray;
      });
    } else {
      const existingPerson = peopleArray.find((existingPerson) => existingPerson.id === person.id);
      if (existingPerson && person.eventId && !existingPerson.eventId) {
        existingPerson.eventId = person.eventId;
      }
      if (
        existingPerson &&
        person.primaryFacility &&
        (!existingPerson.primaryFacility || !existingPerson?.primaryFacility.id)
      ) {
        existingPerson.primaryFacility = person.primaryFacility;
      }
    }
  }

  const { loading: loadingPerson } = useQuery(PERSON_WITH_SALESFORCE_ID_QUERY, {
    variables: { input: peoplePGAId },
    onCompleted: (data) => {
      if (interactionAudience === "single") {
        setVariables({ ...variables, participantId: data.person.id });
      }

      if (interactionAudience === "group") {
        data.person &&
          addPerson({
            id: data.person.id,
            firstName: data.person.firstName,
            lastName: data.person.lastName,
            pgaId: data.person.pgaId,
            sectionName: data.person.primarySectionAffiliation?.section?.name,
            facilityName: data.person.primaryFacility?.name,
            profilePhoto: data.person.profilePhoto,
            selected: true,
            index: peopleArray.length,
            primaryFacility: {
              name: data.person.primaryFacility?.name,
              id: data.person.primaryFacility?.id
            },
            primarySectionAffiliation: {
              section: {
                name: data.person.primarySectionAffiliation?.section?.name
              }
            }
          });
      }
    },
    skip: !peoplePGAId
  });

  useEffect(() => {
    if (sidebarOpen && resolvedPgaId) {
      setPeoplePGAId(resolvedPgaId);
      setSearchedForPerson(true);
      setInteractionAudience("single");
    }
  }, [sidebarOpen, resolvedPgaId]);

  useEffect(() => {
    if (sidebarOpen && resolvedJobPostId) {
      setVariables((prev) => ({
        ...prev,
        jobPostId: resolvedJobPostId,
        privacy_restricted: true
      }));
      setValue("interactionPrivacy", true);
    }
  }, [sidebarOpen, resolvedJobPostId, setValue]);

  const { loading: facilityPeopleLoading } = useQuery(PEOPLE_WITH_FACILITY_ID, {
    variables: { input: { facilityIds: currentFacility?.id } },
    onCompleted: (data) => {
      const eligiblePeople = data.people.nodes;
      if (eligiblePeople.length === 0) {
        setIneligibleFacility(currentFacility);
      } else {
        eligiblePeople.forEach((person: Person, index: number) => {
          const selectedPeopleAmount = peopleArray.filter((person) => person.selected).length;
          const myIndex = selectedPeopleAmount + index;
          addPerson({
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
            pgaId: person.pgaId,
            sectionName: person.primarySectionAffiliation?.section?.name,
            facilityName: person.primaryFacility?.name,
            profilePhoto: person.profilePhoto,
            selected: true,
            index: myIndex,
            primaryFacility: {
              name: person.primaryFacility?.name,
              id: person.primaryFacility?.id
            },
            primarySectionAffiliation: {
              section: {
                name: person.primarySectionAffiliation?.section?.name
              }
            }
          });
        });
        setGatheredFacilities([...gatheredFacilities, currentFacility!]);
      }
    },
    skip:
      !currentFacility ||
      (currentFacility && gatheredFacilities.map((facility) => facility.id).includes(currentFacility.id))
  });

  const { loading: eventRegistrantsLoading } = useQuery(REGISTRANTS_FROM_EVENT_ID, {
    variables: { eventSlug: currentEvent?.slug },
    onCompleted: (data) => {
      setEventRegistrants(data.eventRegistrants.nodes);
      if (data.eventRegistrants.nodes.length === 0) {
        setIneligibleEvent(currentEvent);
      }
    },
    skip: !currentEvent || (currentEvent && gatheredEvents.map((event) => event.id).includes(currentEvent.id))
  });

  const { loading: eventPeopleLoading } = useQuery(PEOPLE_FROM_IDS, {
    variables: {
      input: { pgaIds: eventRegistrants.map((registrant) => registrant.pgaId) }
    },
    onCompleted: (data) => {
      const eligiblePeople = data.people.nodes;
      if (eligiblePeople.length === 0) {
        setIneligibleEvent(currentEvent);
      } else {
        eligiblePeople.forEach((person: Person, index: number) => {
          const selectedPeopleAmount = peopleArray.filter((person) => person.selected).length;
          const myIndex = selectedPeopleAmount + index;
          addPerson({
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
            pgaId: person.pgaId,
            sectionName: person.primarySectionAffiliation?.section?.name,
            facilityName: person.primaryFacility?.name,
            profilePhoto: person.profilePhoto,
            selected: true,
            index: myIndex,
            primaryFacility: {
              name: person.primaryFacility?.name,
              id: person.primaryFacility?.id
            },
            primarySectionAffiliation: {
              section: {
                name: person.primarySectionAffiliation?.section?.name
              }
            },
            eventId: currentEvent?.id
          });
        });
        setGatheredEvents([...gatheredEvents, currentEvent!]);
      }
    },
    skip:
      !currentEvent ||
      (currentEvent && gatheredEvents.map((event) => event.id).includes(currentEvent.id)) ||
      eventRegistrants.map((registrant) => registrant.pgaId).length === 0
  });

  const viewPerson = () => {
    navigate(`/people/${peoplePGAId}/interactions/interactions`, {
      state: { autoOpenSidebar: true }
    });
  };

  const onSidebarChange = (openState: boolean) => {
    if (openState == false) {
      setSidebarOpen(false);
      resetForm();
      resetVariables();
    } else {
      setSidebarOpen(openState);
    }
  };

  async function createInteraction() {
    if (taskHook.attachTask && !taskHook.validate()) return;

    try {
      let categoryPromise = Promise.resolve("");
      const summaryPromise = generateSummary({
        mode: variables.mode ?? null,
        subject: null,
        comments: variables.description ?? null
      });

      const runTopicGenerator = (!variables.categories || variables.categories.length == 0) && isCareerServices;

      if (runTopicGenerator) {
        categoryPromise = generateCategory({
          mode: variables.mode ?? null,
          subject: null,
          comments: variables.description ?? null
        });
      } else {
        categoryPromise = Promise.resolve("");
      }

      const [summaryResponse, categoryResponse] = await Promise.all([summaryPromise, categoryPromise]);

      const formattedSummary = formatAiSummary(summaryResponse);

      const categories =
        categoryResponse?.length > 0 // if the response is not empty or an empty string we'll use the AI response.
          ? [guardAiResponse(categoryResponse, validCategoryNames)]
          : variables.categories;

      const privacyRestricted = Boolean(variables.jobPostId) || Boolean(variables.privacy_restricted);

      const personName = getValues("interactionPerson");
      const defaultTaskName = personName ? `Follow-up to interaction with ${personName}` : "Follow-up to interaction";
      const taskInput = taskHook.getTaskInput(defaultTaskName);
      const input: BackofficeCRMCreateInteractionInput = {
        participantId: variables.participantId,
        interactionDate: variables.interactionDate,
        mode: variables.mode,
        categories: categories,
        description: variables.description,
        summary: formattedSummary,
        privacyRestricted,
        jobPostId: variables.jobPostId,
        ...(taskInput && { task: taskInput })
      };
      const { data } = await createInteractionMutation({
        variables: { input }
      });

      if (data?.createInteraction?.success) {
        showSnackbar(`Interaction added`, {
          actions: [
            <Button key="show-button" variant="text" onClick={viewPerson}>
              View Person
            </Button>
          ]
        });

        const createdTaskId = data.createInteraction.task?.id;
        if (taskInput) {
          showSnackbar("Task created", {
            ...(createdTaskId && {
              actions: [
                <Button key="view-task" variant="text" onClick={() => navigate(`/tasks/${createdTaskId}`)}>
                  View Task
                </Button>
              ]
            })
          });
        }

        trackEvent(INTERACTION_ANALYTICS_EVENTS.created, {
          source: INTERACTION_CREATE_SOURCES.globalCreate,
          audience: interactionAudience,
          mode: variables.mode,
          activityType: categories,
          description: variables.description,
          summary: formattedSummary,
          privacyRestricted,
          participants: [variables.participantId],
          success: Boolean(summaryResponse && categoryResponse)
        });

        if (shouldRefetchPersonInteractionViewsForSingleCreate(resolvedPgaId, peoplePGAId?.toString())) {
          refetchPersonInteractionViews(apolloClient);
        }

        if (resolvedJobPostId) {
          apolloClient.refetchQueries({
            include: [JOB_POST_WITH_INTERACTIONS_QUERY, JOB_POST_DETAILS_QUERY]
          });
        }

        onSidebarChange(false);
      } else {
        console.error("Error creating interaction:", data?.createInteraction?.message);
        const apiMessage = data?.createInteraction?.message;
        if (apiMessage) {
          showErrorSnackbar(apiMessage);
        } else {
          showErrorSnackbar("Interaction not created", { subtitle: "Please try again later." });
        }
      }
    } catch (error) {
      console.error("Error creating interaction:", error);
      showErrorSnackbar("Interaction not created", { subtitle: "Please try again later." });
    }
  }

  async function createGroupInteraction() {
    if (taskHook.attachTask && !taskHook.validate()) return;

    try {
      let categoryPromise = Promise.resolve("");
      const summaryPromise = generateSummary({
        mode: variables.mode ?? null,
        subject: null,
        comments: variables.description ?? null
      });

      const runTopicGenerator = (!variables.categories || variables.categories.length == 0) && isCareerServices;

      if (runTopicGenerator) {
        categoryPromise = generateCategory({
          mode: variables.mode ?? null,
          subject: "group interaction with " + peopleArray.map((person) => person.firstName).join(", "),
          comments: variables.description ?? null
        });
      }

      const [summaryResponse, categoryResponse] = await Promise.all([summaryPromise, categoryPromise]);

      const formattedSummary = formatAiSummary(summaryResponse);
      const categories =
        categoryResponse?.length > 0 // if the response is not empty or an empty string we'll use the AI response.
          ? [guardAiResponse(categoryResponse, validCategoryNames)]
          : variables.categories;

      const groupPrivacyRestricted = Boolean(variables.jobPostId) || Boolean(variables.privacy_restricted);

      const selectedCount = peopleArray.filter((p) => p.selected).length;
      const groupLabel = `${selectedCount} ${selectedCount === 1 ? "person" : "people"}`;
      const defaultGroupTaskName = `Follow-up to interaction with ${groupLabel}`;
      const taskInput = taskHook.getTaskInput(defaultGroupTaskName);
      const input: BackofficeCRMCreateGroupInteractionInput = {
        participantIds: variables.participantIds,
        interactionDate: variables.interactionDate,
        mode: variables.mode,
        categories: categories,
        description: variables.description,
        summary: formattedSummary,
        privacyRestricted: groupPrivacyRestricted,
        jobPostId: variables.jobPostId,
        ...(taskInput && { task: taskInput })
      };
      const { data } = await createGroupInteractionMutation({
        variables: { input }
      });

      if (data?.createGroupInteraction?.success) {
        showSnackbar("Group Interaction added");
        const createdTaskId = data.createGroupInteraction.task?.id;
        if (taskInput) {
          showSnackbar("Task created", {
            ...(createdTaskId && {
              actions: [
                <Button key="view-task" variant="text" onClick={() => navigate(`/tasks/${createdTaskId}`)}>
                  View Task
                </Button>
              ]
            })
          });
        }
        trackEvent(INTERACTION_ANALYTICS_EVENTS.created, {
          source: INTERACTION_CREATE_SOURCES.globalCreate,
          audience: interactionAudience,
          mode: variables.mode,
          activityType: categories,
          description: variables.description,
          summary: formattedSummary,
          privacyRestricted: groupPrivacyRestricted,
          participants: variables.participantIds,
          success: Boolean(summaryResponse && categoryResponse)
        });

        if (shouldRefetchPersonInteractionViewsForGroupCreate(resolvedPgaId, peopleArray)) {
          refetchPersonInteractionViews(apolloClient);
        }

        onSidebarChange(false);
      } else {
        console.error("Error creating interaction:", data?.createGroupInteraction?.message);
        const apiMessage = data?.createGroupInteraction?.message;
        if (apiMessage) {
          showErrorSnackbar(apiMessage);
        } else {
          showErrorSnackbar("Interaction not created", { subtitle: "Please try again later." });
        }
      }
    } catch (error) {
      console.error("Error creating interaction:", error);
      showErrorSnackbar("Interaction not created", { subtitle: "Please try again later." });
    }
  }
  const resetForm = () => {
    setValue("interactionPerson", "");
    setValue("interactionMethod", defaultMode);
    setValue("interactionDate", {
      startDate: defaultInteractionDate,
      endDate: defaultInteractionDate
    });
    setValue("interactionTopic", []);
    setValue("interactionDescription", "");
    setValue("interactionPrivacy", false);
    setSelectedCategories([]);
    taskHook.reset();
  };

  const resetVariables = () => {
    setVariables({
      interactionDate: defaultInteractionDate.toISOString().split("T")[0],
      mode: defaultMode,
      activityType: null,
      categories: [],
      description: null,
      participantId: null,
      participantIds: null,
      privacy_restricted: false,
      jobPostId: null
    });
    setSelectedCategories([]);
    setSearchedForPerson(false);
    setPeoplePGAId(undefined);
    setPeopleArray([]);
    setCurrentFacility(undefined);
    setCurrentEvent(undefined);
    setInteractionAudience(audienceOptions[0].value);
    setClearedPreSelection(false);
    lockedPgaIdRef.current = undefined;
    lockedJobPostIdRef.current = undefined;
  };

  const asyncLoading = generatingAiSummary || generatingAiCategory;

  const asyncCreateInteractionLoading = asyncLoading || createMutationLoading; // has both mutation loading states (single and group)

  const dynamicIcon = asyncCreateInteractionLoading ? <Progress size={20} color="white" /> : "check";

  const CreateInteractionActions = () => {
    if (!multiPersonMode) {
      if (interactionAudience === "single") {
        return (
          <div className="flex justify-end space-x-4">
            <Button
              onClick={handleSubmit(createInteraction)}
              variant="filled"
              icon={dynamicIcon}
              disabled={
                asyncCreateInteractionLoading ||
                (interactionAudience === "single" && searchedForPerson && !variables.participantId) ||
                (!isCareerServices && !variables.activityType && variables.categories?.length == 0)
              }
            >
              Save
            </Button>
            <Button
              onClick={() => onSidebarChange(false)}
              variant="text"
              disabled={
                asyncCreateInteractionLoading ||
                (interactionAudience === "single" && searchedForPerson && !variables.participantId)
              }
            >
              Cancel
            </Button>
          </div>
        );
      } else if (interactionAudience === "group") {
        return (
          <div className="flex justify-end space-x-4">
            <Button
              onClick={handleSubmit(createGroupInteraction)}
              variant="filled"
              icon={dynamicIcon}
              disabled={asyncCreateInteractionLoading || (searchedForPerson && !variables.participantIds)}
            >
              Save
            </Button>
            <Button
              onClick={() => onSidebarChange(false)}
              variant="text"
              disabled={asyncCreateInteractionLoading || (searchedForPerson && !variables.participantIds)}
            >
              Cancel
            </Button>
          </div>
        );
      }
    } else if (multiPersonMode) {
      return (
        <div className="flex justify-end space-x-4">
          <Button
            onClick={() => {
              validatePeopleField();
              setMultiPersonMode(false);
            }}
            variant="outlined"
          >
            Add to Interaction
          </Button>
        </div>
      );
    }
  };

  const descriptionRef = useRef<HTMLInputElement>(null);
  const prevParticipantIdRef = useRef<string | null | undefined>(null);

  useEffect(() => {
    const prev = prevParticipantIdRef.current;
    prevParticipantIdRef.current = variables.participantId;

    if (interactionAudience === "single" && variables.participantId && !prev) {
      requestAnimationFrame(() => {
        descriptionRef.current?.focus();
      });
    }
  }, [interactionAudience, variables.participantId]);

  const handleDescriptionFocus = () => {
    if (isCompactLayout && descriptionRef.current) {
      const el = descriptionRef.current;
      setTimeout(() => {
        el.scrollIntoView({ block: "start", behavior: "smooth" });
      }, 300);
    }
  };

  return (
    <SideSheetModal
      isSidebarOpen={sidebarOpen}
      title={multiPersonMode ? "Add People" : "Add Interaction"}
      goBack={
        multiPersonMode
          ? () => {
              validatePeopleField();
              setMultiPersonMode(false);
            }
          : undefined
      }
      disableClose={multiPersonMode}
      actions={<CreateInteractionActions />}
      setSidebarOpen={onSidebarChange}
    >
      {multiPersonMode && (
        <div className="pt-6">
          <MultiPersonAdder
            peopleArray={peopleArray}
            variables={variables}
            setVariables={setVariables}
            setSearchedForPerson={setSearchedForPerson}
            setPeoplePGAId={setPeoplePGAId}
            setCurrentEvent={setCurrentEvent}
            loading={loadingPerson}
            eventPeopleLoading={eventPeopleLoading}
            eventRegistrantsLoading={eventRegistrantsLoading}
            facilityPeopleLoading={facilityPeopleLoading}
            ineligiblePerson={ineligiblePerson}
            setIneligiblePerson={setIneligiblePerson}
            ineligibleEvent={ineligibleEvent}
            setIneligibleEvent={setIneligibleEvent}
            ineligibleFacility={ineligibleFacility}
            setIneligibleFacility={setIneligibleFacility}
            setCurrentFacility={setCurrentFacility}
            gatheredFacilities={gatheredFacilities}
            gatheredEvents={gatheredEvents}
          />
        </div>
      )}

      {!multiPersonMode && (
        <div className={`flex flex-col h-full ${hasPreSelectedPerson ? "" : "pt-6"}`}>
          {!hasPreSelectedPerson && (
            <div className="mb-8">
              <SegmentedButton
                options={audienceOptions}
                value={interactionAudience}
                onChange={(audience) => {
                  audience && handleAudienceChange(audience);
                }}
                selectedColor="bg-sys-brand-secondary-container"
                textColor="text-sys-brand-on-secondary-container"
                backgroundColor="bg-sys-brand-surface-container-high"
                showCheckmark={true}
                fullWidth={true}
              />
            </div>
          )}

          <div
            className={`space-y-6 mb-2 flex-1 flex flex-col min-h-0 ${
              asyncCreateInteractionLoading ? "opacity-50" : ""
            }`}
          >
            {interactionAudience === "single" && hasPreSelectedPerson && resolvedPgaId && (
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <ObjectHeadingWrapper
                    pgaId={resolvedPgaId}
                    variant="condensed"
                    showMinimalMetadata={true}
                    hideTags={true}
                    avatarSize="medium"
                  />
                </div>
                <button
                  className="cursor-pointer flex items-center shrink-0 p-1 rounded-full hover:bg-sys-brand-surface-container-high transition-colors"
                  onClick={clearPreSelectedPerson}
                  aria-label="Remove selected person"
                  disabled={asyncCreateInteractionLoading}
                >
                  <Icon name="close" size={20} />
                </button>
              </div>
            )}
            {interactionAudience === "single" && !hasPreSelectedPerson && (
              <SinglePerson
                setSearchedForPerson={setSearchedForPerson}
                setVariables={setVariables}
                variables={variables}
                setPeoplePGAId={setPeoplePGAId}
                disabled={asyncCreateInteractionLoading}
                setValue={setValue}
              />
            )}
            {interactionAudience === "group" && (
              <GroupInteraction
                setMultiPersonMode={setMultiPersonMode}
                peopleArray={peopleArray}
                control={control}
                disabled={asyncCreateInteractionLoading}
                setVariables={setVariables}
                variables={variables}
                setValue={setValue}
              />
            )}
            {(interactionAudience !== "single"
              ? !searchedForPerson || (searchedForPerson && variables.participantIds) || loadingPerson
              : variables.participantId || loadingPerson) && (
              <div className="flex flex-col flex-1 min-h-0 space-y-6">
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Controller
                        name="interactionMethod"
                        control={control}
                        rules={{ required: "Method is required" }}
                        render={({ field, fieldState }) => (
                          <Select
                            label="Method"
                            required={true}
                            options={modeOptions}
                            value={variables.mode || undefined}
                            className="w-full"
                            onSelect={(e) => {
                              field.onChange(e);
                              setVariables({ ...variables, mode: e });
                            }}
                            error={fieldState.invalid}
                            errorText={fieldState.error?.message}
                            disabled={asyncCreateInteractionLoading}
                            dropdownHeight="full"
                          />
                        )}
                      />
                    </div>
                    <div className="flex-1">
                      <Controller
                        name="interactionDate"
                        control={control}
                        rules={{
                          validate: {
                            startDateNotNull: (value) => value?.startDate !== null || "Date is required",
                            startDateNotInFuture: (value) =>
                              new Date(value?.startDate) < new Date() || "Date cannot be in the future"
                          }
                        }}
                        disabled={asyncCreateInteractionLoading}
                        render={({ field, fieldState }) => (
                          <DateSelect
                            disabled={asyncCreateInteractionLoading}
                            label="Date *"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e);
                              if (e && typeof e === "object" && "startDate" in e && e.startDate) {
                                setVariables({
                                  ...variables,
                                  interactionDate: e.startDate
                                });
                              }
                              trigger("interactionDate");
                            }}
                            popoverDirection="down"
                            maxDate={new Date()}
                            useSingleDate
                            showFooter={false}
                            error={fieldState.invalid}
                            errorMessage={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                  </div>
                  {canManageJobPosts && (
                    <AlgoliaJobsSearch
                      label="Job"
                      placeholder="Search for jobs"
                      disabled={asyncCreateInteractionLoading}
                      jobPostId={variables.jobPostId ?? undefined}
                      onSelect={(jobId, _jobTitle) => {
                        setVariables({
                          ...variables,
                          jobPostId: jobId,
                          privacy_restricted: true
                        });
                        setValue("interactionPrivacy", true);
                      }}
                      onClear={() => {
                        const topicsForcePrivate = selectionRequiresDefaultPrivateTopics(
                          selectedCategories,
                          interactionCategoriesMetadata
                        );
                        setVariables({
                          ...variables,
                          jobPostId: undefined,
                          privacy_restricted: topicsForcePrivate
                        });
                        setValue("interactionPrivacy", topicsForcePrivate);
                      }}
                    />
                  )}
                  {!loadingCategories && (
                    <Controller
                      name="interactionTopic"
                      control={control}
                      disabled={asyncCreateInteractionLoading}
                      rules={!isCareerServices ? { required: "Topic is required" } : {}}
                      render={({ fieldState, field: { onChange } }) => (
                        <MultiSelect
                          label="Topic"
                          skipShowAll
                          searchable={!isCompactLayout}
                          defaultLabel="Topics"
                          options={interactionCategoriesMetadata.map((topic) => ({
                            label: topic.name,
                            value: topic.name,
                            ...(topic.isDefaultPrivate ? { description: "Private by default" } : {})
                          }))}
                          onSelect={(value, checked) => {
                            const category = checked
                              ? [...selectedCategories, value]
                              : selectedCategories.filter((v) => v !== value);
                            const cleaned = reject(category, isEmpty);
                            onChange(cleaned);
                            setSelectedCategories(cleaned);

                            // Only update privacy if we're not already private and the update wants to make it private
                            const currentPrivacy = getValues("interactionPrivacy");
                            const privacyUpdate = shouldUpdatePrivacyForSelection(
                              selectedCategories,
                              cleaned,
                              interactionCategoriesMetadata
                            );
                            if (
                              !selectedCategories.includes(COMPENSATION_NEGOTIATION_CATEGORY) &&
                              cleaned.includes(COMPENSATION_NEGOTIATION_CATEGORY)
                            ) {
                              applyCompensationNegotiationTaskDefaults(
                                Boolean(variables.jobPostId) || Boolean(currentPrivacy) || privacyUpdate === true
                              );
                            }
                            if (privacyUpdate !== null && !currentPrivacy) {
                              setValue("interactionPrivacy", privacyUpdate);
                              setVariables((prev) => ({
                                ...prev,
                                categories: cleaned,
                                privacy_restricted: privacyUpdate
                              }));
                            } else {
                              setVariables((prev) => ({
                                ...prev,
                                categories: cleaned
                              }));
                            }
                          }}
                          onClear={() => {
                            onChange([]);
                            setSelectedCategories([]);

                            // No privacy update on clear - privacy stays whatever user set it to
                            setVariables((prev) => ({
                              ...prev,
                              categories: []
                            }));
                          }}
                          selectedValues={selectedCategories}
                          required={!isCareerServices}
                          error={fieldState.invalid}
                          errorText={fieldState.error?.message}
                          disabled={asyncCreateInteractionLoading || loadingCategories}
                        />
                      )}
                    />
                  )}
                </div>

                <Controller
                  name="interactionDescription"
                  control={control}
                  rules={{ required: "Description is required" }}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <div>
                      <style>
                        {`.description-text-field { --md-outlined-text-field-input-text-placeholder-color: var(--md-sys-color-outline); }`}
                      </style>
                      <TextField
                        value={value}
                        disabled={asyncCreateInteractionLoading}
                        onChangeText={(text) => {
                          onChange(text);
                          text.trim() === ""
                            ? setVariables({ ...variables, description: null })
                            : setVariables({ ...variables, description: text });
                        }}
                        required
                        label="Description"
                        type="textarea"
                        rows={4}
                        className="w-full description-text-field"
                        error={fieldState.invalid}
                        errorText={fieldState.error?.message}
                        ref={descriptionRef}
                        onFocus={handleDescriptionFocus}
                        supportingText="Tip: Access your device's microphone to dictate"
                      />
                    </div>
                  )}
                />
                <div className="space-y-5 mt-auto pb-6">
                  <Controller
                    name="interactionPrivacy"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <Switch
                          disabled={asyncCreateInteractionLoading}
                          headline="Make Private"
                          selected={value}
                          supportingText="Only your department can view"
                          onChange={() => {
                            const newValue = !value;
                            onChange(newValue);
                            setVariables({
                              ...variables,
                              privacy_restricted: newValue
                            });
                          }}
                        />
                      );
                    }}
                  />

                  {canManageTasks && (
                    <AttachTaskSection
                      objectLabel="interaction"
                      attachTask={taskHook.attachTask}
                      onToggle={(enabled) => {
                        taskHook.toggleAttachTask(enabled, "");
                      }}
                      taskName={taskHook.taskName}
                      onTaskNameChange={taskHook.setTaskName}
                      taskAssignee={taskHook.taskAssignee}
                      assigneeSearchQuery={taskHook.taskAssigneeSearch}
                      onAssigneeSearchChange={taskHook.setTaskAssigneeSearch}
                      selectAssignee={taskHook.selectAssignee}
                      clearAssignee={taskHook.clearAssignee}
                      assigneeResults={taskHook.usersData}
                      assigneeLoading={taskHook.usersLoading}
                      taskDueDate={taskHook.taskDueDate}
                      onDueDateChange={taskHook.setTaskDueDate}
                      taskIsPrivate={taskHook.taskIsPrivate}
                      onTaskIsPrivateChange={taskHook.setTaskIsPrivate}
                      errors={taskHook.taskErrors}
                      clearError={taskHook.clearError}
                      disabled={asyncCreateInteractionLoading}
                      selectedTeamTags={taskHook.selectedTeamTags}
                      teamSearchTerm={taskHook.teamSearchTerm}
                      onTeamSearchChange={taskHook.setTeamSearchTerm}
                      availableDepartmentOptions={taskHook.availableDepartmentOptions}
                      onTeamVisibilitySelect={taskHook.handleTeamVisibilitySelect}
                      onRemoveTeamTag={taskHook.removeTeamTag}
                    />
                  )}
                </div>
              </div>
            )}
            {searchedForPerson &&
              !variables.participantId &&
              !loadingPerson &&
              !peoplePGAId &&
              interactionAudience === "single" && (
                <Banner
                  variant="error"
                  description={`Interactions cannot be added for ${getValues(
                    "interactionPerson"
                  )} at this time. Try selecting another person to add a interaction to.`}
                  inline={true}
                />
              )}
          </div>
        </div>
      )}
    </SideSheetModal>
  );
};
